import React from 'react'
import cx from 'classnames'
import { range } from 'lodash'
import Link from 'next/link'
import { Icon } from '@scentregroup/ui'
import style from './index.module.scss'

interface IPaginationProps {
  currentPage: number
  totalPages: number
  onChange: (page: number) => void
}
interface ILinkPaginationProps {
  currentPage: number
  totalPages: number
  makePageLink: (page: number) => string
  onLinkClick?: (page: number) => void
}

export const generatePages = (
  currentPage: number,
  totalPages: number
): number[] => {
  const maxPageButtons = 5
  // checks if current page is out of range
  if (currentPage < 0) {
    currentPage = 0
  } else if (currentPage > totalPages) {
    currentPage = totalPages - 1
  }

  let startPage: number, endPage: number
  if (totalPages < maxPageButtons) {
    startPage = 0
    endPage = totalPages
  } else {
    const numberOfPagesBeforeCurrentPage = Math.floor(maxPageButtons / 2)
    const numberOfPagesAfterCurrentPage = Math.ceil(maxPageButtons / 2)
    if (currentPage <= numberOfPagesBeforeCurrentPage) {
      startPage = 0
      endPage = maxPageButtons
    } else if (currentPage + numberOfPagesAfterCurrentPage >= totalPages) {
      startPage = totalPages - maxPageButtons
      endPage = totalPages
    } else {
      startPage = currentPage - numberOfPagesBeforeCurrentPage
      endPage = currentPage + numberOfPagesAfterCurrentPage
    }
  }
  return range(startPage, endPage)
}

export const LinkPagination: React.FunctionComponent<ILinkPaginationProps> = ({
  currentPage,
  totalPages,
  makePageLink,
  onLinkClick,
}) => {
  const onLinkClick_ = onLinkClick || (() => undefined)
  const pages = generatePages(currentPage, totalPages)
  const onFirstPage = currentPage == 0
  const onLastPage = totalPages - 1 == currentPage
  return (
    <div className={style.paginationWrapper}>
      {onFirstPage ? (
        <span className={cx(style.button, style.disabledLink)}>
          <Icon type={'chevron-left'} size="small" />
        </span>
      ) : (
        <Link passHref href={makePageLink(currentPage - 1)} prefetch={false}>
          <a
            className={style.button}
            href={makePageLink(currentPage - 1)}
            onClick={() => onLinkClick_(currentPage - 1)}
            aria-label="Go to previous page"
          >
            <Icon type={'chevron-left'} size="small" />
          </a>
        </Link>
      )}
      {pages.map(page => (
        <Link
          passHref
          key={`go-to-page-${page + 1}`}
          href={makePageLink(page)}
          prefetch={false}
        >
          <a
            href={makePageLink(page)}
            aria-label={`Go to page ${page + 1}`}
            className={cx(style.button, {
              [style.isCurrentPage]: page == currentPage,
            })}
            onClick={() => onLinkClick_(page)}
          >
            {page + 1}
          </a>
        </Link>
      ))}
      {onLastPage ? (
        <span className={cx(style.button, style.disabledLink)}>
          <Icon type={'chevron-right'} size="small" />
        </span>
      ) : (
        <Link passHref href={makePageLink(currentPage + 1)} prefetch={false}>
          <a
            className={style.button}
            href={makePageLink(currentPage + 1)}
            onClick={() => onLinkClick_(currentPage + 1)}
            aria-label="Go to next page"
          >
            <Icon type={'chevron-right'} size="small" />
          </a>
        </Link>
      )}
    </div>
  )
}

LinkPagination.defaultProps = {
  currentPage: 0,
  totalPages: 1,
}

export const Pagination: React.FunctionComponent<IPaginationProps> = ({
  currentPage,
  totalPages,
  onChange,
}) => {
  const pages = generatePages(currentPage, totalPages)
  const onFirstPage = currentPage == 0
  const onLastPage = totalPages - 1 == currentPage

  return (
    <div className={style.paginationWrapper}>
      <button
        className={style.button}
        onClick={() => onChange(currentPage - 1)}
        aria-label="Go to previous page"
        disabled={onFirstPage}
      >
        <Icon type={'chevron-left'} size="small" />
      </button>
      {pages.map(page => (
        <button
          key={`go-to-page-${page + 1}`}
          aria-label={`Go to page ${page + 1}`}
          className={cx(style.button, {
            [style.isCurrentPage]: page == currentPage,
          })}
          onClick={() => onChange(page)}
        >
          {page + 1}
        </button>
      ))}
      <button
        className={style.button}
        aria-label="Go to next page"
        onClick={() => onChange(currentPage + 1)}
        disabled={onLastPage}
      >
        <Icon type={'chevron-right'} size="small" />
      </button>
    </div>
  )
}

Pagination.defaultProps = {
  currentPage: 0,
  totalPages: 1,
}
